import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Medal, Star, Target, Award, Crown, Zap, Eye, Brain, Users, Globe, Coins, MessageCircle, Crosshair, Flame, ArrowLeft } from 'lucide-react';
import { MobileBottomNav } from '@/components/MobileNav';

const API_URL = process.env.REACT_APP_BACKEND_URL?.replace(/^http:\/\//i, 'https://') + '/api';

const ICON_MAP = {
  star: Star, flame: Flame, zap: Zap, crown: Crown, target: Target, award: Award,
  trophy: Trophy, crosshair: Crosshair, eye: Eye, brain: Brain, users: Users,
  globe: Globe, coins: Coins, 'message-circle': MessageCircle,
};

const SocialPage = () => {
  const navigate = useNavigate();
  const [leaders, setLeaders] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [tab, setTab] = useState('leaderboard');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchLeaderboard();
    fetchAchievements();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const res = await fetch(`${API_URL}/social/leaderboard`);
      if (res.ok) {
        const data = await res.json();
        setLeaders(data.leaderboard || []);
      }
    } catch (e) { console.error(e); }
  };

  const fetchAchievements = async () => {
    if (!user.user_id) return;
    try {
      const res = await fetch(`${API_URL}/social/achievements/${user.user_id}`);
      if (res.ok) {
        const data = await res.json();
        setAchievements(data.achievements || []);
      }
    } catch (e) { console.error(e); }
  };

  return (
    <div className="min-h-screen bg-[#121212] pb-24">
      <header className="bg-[#1E1E1E] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Trophy className="w-6 h-6 text-yellow-500" />
          <h1 className="text-xl font-bold text-white">Community</h1>
        </div>
      </header>

      <div data-testid="social-page" className="max-w-2xl mx-auto px-4 py-6">

      {/* Tabs */}
      <div className="flex gap-1 mb-4 p-1 bg-[#1E1E1E] rounded-xl border border-white/5">
        {[
          { id: 'leaderboard', label: 'Rangliste', icon: Trophy },
          { id: 'achievements', label: 'Achievements', icon: Star },
        ].map(t => (
          <button
            key={t.id}
            data-testid={`social-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all ${
              tab === t.id ? 'bg-[#00BFA6]/10 text-[#00BFA6]' : 'text-gray-500 hover:text-white'
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Leaderboard Tab */}
      {tab === 'leaderboard' && (
        <div data-testid="leaderboard-list" className="space-y-2">
          {leaders.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">
              Noch keine Tipster im Ranking. Gib deinen ersten Tipp ab!
            </div>
          ) : leaders.map((l, i) => (
            <div key={i} data-testid={`leaderboard-entry-${i}`}
              className="flex items-center gap-3 p-3 bg-[#1E1E1E] rounded-xl border border-white/5"
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                i === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                i === 1 ? 'bg-gray-400/20 text-gray-400' :
                i === 2 ? 'bg-orange-500/20 text-orange-400' :
                'bg-white/5 text-gray-500'
              }`}>
                {i + 1}
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium text-white">{l.username || 'Anonym'}</div>
                <div className="text-[10px] text-gray-500">{l.total_tips} Tipps</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold font-mono text-[#00BFA6]">{l.accuracy}%</div>
                <div className="text-[10px] text-gray-500">Genauigkeit</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Achievements Tab */}
      {tab === 'achievements' && (
        <div data-testid="achievements-grid" className="grid grid-cols-2 gap-2">
          {achievements.map((ach, i) => {
            const IconComp = ICON_MAP[ach.icon] || Star;
            return (
              <div key={i} data-testid={`achievement-${ach.id}`}
                className={`p-3 rounded-xl border ${
                  ach.earned
                    ? 'bg-[#00BFA6]/5 border-[#00BFA6]/30'
                    : 'bg-[#1E1E1E] border-white/5 opacity-50'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <IconComp className={`w-4 h-4 ${ach.earned ? 'text-[#00BFA6]' : 'text-gray-600'}`} />
                  <span className={`text-xs font-medium ${ach.earned ? 'text-white' : 'text-gray-500'}`}>
                    {ach.name}
                  </span>
                </div>
                <div className="text-[10px] text-gray-500">{ach.desc}</div>
                <div className="mt-1 text-[10px] font-mono text-[#7B1FA2]">+{ach.points} XP</div>
              </div>
            );
          })}
        </div>
      )}
      </div>
      <MobileBottomNav />
    </div>
  );
};

export default SocialPage;
